import { useId, type InputHTMLAttributes, type ReactNode, type Ref, type TextareaHTMLAttributes } from "react";
import { controlClass, hintId, type ControlSize } from "./control.ts";

/**
 * A labelled text input.
 *
 * The label is always visible rather than a placeholder that disappears the moment someone types,
 * which on a phone is the difference between a form you can check before submitting and one you
 * cannot.
 *
 * Validation is shown, never enforced by disappearing: an error replaces the hint and turns the
 * border, and the field keeps whatever was typed. Half a date of birth is a state a member passes
 * through on the way to a whole one, so the message has to be a nudge rather than a wall.
 *
 * Email, number, phone, password, date and time are this same field with a `type`. Give each the
 * right `inputMode` and `autoComplete`, so a phone opens the right keyboard.
 */

/**
 * The label, the control and the hint or error under it. Shared by every labelled input
 * (`Field`, `TextAreaField`, `SelectField`, `FileField`), so they line up in one form.
 */
export function FieldShell({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="eyebrow text-ink-2">
        {label}
      </label>
      {children}
      <FieldMessage id={id} hint={hint} error={error} />
    </div>
  );
}

/** The line under a control: the error in red when there is one, else the hint. */
export function FieldMessage({ id, hint, error }: { id: string; hint?: string; error?: string }) {
  if (error) {
    return (
      <p id={hintId(id)} className="text-[13px] font-medium text-cancelled">
        {error}
      </p>
    );
  }
  if (hint) {
    return (
      <p id={hintId(id)} className="text-[13px] text-ink-2">
        {hint}
      </p>
    );
  }
  return null;
}

/** `aria-describedby` for a control whose hint or error may be shown. */
export function describedBy(id: string, hint?: string, error?: string): string | undefined {
  return error || hint ? hintId(id) : undefined;
}

export type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "size"> & {
  label: string;
  hint?: string;
  error?: string;
  /** `regular` (52px) for forms, `compact` (40px) for a table or a toolbar. */
  size?: ControlSize;
  /** The input itself, to focus it from a tap elsewhere. */
  ref?: Ref<HTMLInputElement>;
};

export function Field({ label, hint, error, size = "regular", className = "", ...rest }: FieldProps) {
  const id = useId();

  return (
    <FieldShell id={id} label={label} hint={hint} error={error}>
      <input
        id={id}
        aria-describedby={describedBy(id, hint, error)}
        aria-invalid={error ? true : undefined}
        className={controlClass({
          size,
          error: Boolean(error),
          className: [size === "regular" ? "px-3.5" : "px-2.5", className].filter(Boolean).join(" "),
        })}
        {...rest}
      />
    </FieldShell>
  );
}

export type TextAreaFieldProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> & {
  label: string;
  hint?: string;
  error?: string;
};

/** The same field with room for a sentence, used for the note a member leaves for the coach. */
export function TextAreaField({
  label,
  hint,
  error,
  rows = 3,
  className = "",
  ...rest
}: TextAreaFieldProps) {
  const id = useId();

  return (
    <FieldShell id={id} label={label} hint={hint} error={error}>
      <textarea
        id={id}
        rows={rows}
        aria-describedby={describedBy(id, hint, error)}
        aria-invalid={error ? true : undefined}
        className={controlClass({
          sized: false,
          error: Boolean(error),
          className: ["resize-none px-3.5 py-3 leading-6", className].filter(Boolean).join(" "),
        })}
        {...rest}
      />
    </FieldShell>
  );
}
